import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { ChevronsUpDown, Gift, Loader2, RefreshCw, Check } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { usePersonasQuery } from "@/api/personas";
import { createPromoCode, grantCredits, creditQueries } from "@/api/credits";
import { useSelectedPersona } from "@/hooks/use-selected-persona";

// Form Schemas
const promoFormSchema = z.object({
  amount: z.number().min(1, "Amount must be at least 1 credit"),
  expirationDays: z
    .number()
    .min(1, "Expiration must be at least 1 day")
    .optional(),
});

const grantFormSchema = z.object({
  agentId: z.string().min(1, "Agent is required"),
  amount: z.number().min(1, "Amount must be at least 1 credit"),
  reason: z.string().optional(),
});

const QUICK_AMOUNTS = [1000, 5000, 10000, 50000];

export function CreditGrantManagement() {
  const [selectedPersonaId, setSelectedPersonaId] = useSelectedPersona();
  const [personaOpen, setPersonaOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: personas = [], isLoading: isLoadingPersonas } =
    usePersonasQuery();
  const { data: promoCodes = [], isLoading: isLoadingCodes } = useQuery(
    creditQueries.promoCodes(),
  );

  // Add this before the return statement
  //console.log("Current promoCodes state:", promoCodes);

  const selectedPersona = personas.find(
    (p) => p.persona_id === selectedPersonaId,
  );

  // Mutations
  const createPromoMutation = useMutation({
    mutationFn: (data: z.infer<typeof promoFormSchema>) => {
      const promoId =
        `PROMO-${Math.random().toString(36).substr(2, 9)}`.toUpperCase();
      return createPromoCode({
        promo_id: promoId,
        amount: data.amount,
        expiration_days: data.expirationDays,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(creditQueries.promoCodes());
      toast({
        title: "Promo Code Created",
        description: "New promo code has been generated successfully.",
      });
      promoForm.reset();
    },
  });

  const grantCreditsMutation = useMutation({
    mutationFn: (data: z.infer<typeof grantFormSchema>) => {
      console.log("Granting credits with data:", data);
      return grantCredits({
        agent_id: data.agentId,
        amount: data.amount,
        reason: data.reason,
      });
    },
    onSuccess: (data, variables) => {
      console.debug("Successfully granted credits:", data);
      queryClient.invalidateQueries({
        queryKey: ["agent-balance", variables.agentId],
      });
      toast({
        title: "Credits Granted",
        description: `${variables.amount.toLocaleString()} credits have been granted successfully.`,
      });
      grantForm.reset({
        agentId: variables.agentId, // Keep the agent selected after success
        amount: 1000,
      });
    },
    onError: (error) => {
      console.error("Error granting credits:", error);
      toast({
        title: "Error Granting Credits",
        description:
          error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive",
      });
    },
  });

  // Forms
  const promoForm = useForm<z.infer<typeof promoFormSchema>>({
    resolver: zodResolver(promoFormSchema),
    defaultValues: {
      amount: 1000,
      expirationDays: 30,
    },
  });

  const grantForm = useForm<z.infer<typeof grantFormSchema>>({
    resolver: zodResolver(grantFormSchema),
    defaultValues: {
      agentId: selectedPersonaId || "", // Initialize with currently selected persona
      amount: 1000,
    },
  });

  // Update form when selectedPersonaId changes
  useEffect(() => {
    if (selectedPersonaId) {
      grantForm.setValue("agentId", selectedPersonaId);
    }
  }, [selectedPersonaId, grantForm]);

  const onGrantFormSubmit = async (data: z.infer<typeof grantFormSchema>) => {
    // Validate the form manually before submitting
    if (!data.agentId) {
      toast({
        title: "Agent Required",
        description: "Please select an agent to grant credits to",
        variant: "destructive",
      });
      return;
    }

    try {
      await grantCreditsMutation.mutateAsync(data);
    } catch (error) {
      console.debug("Error granting credits:", error);
      // Error is handled in the mutation's onError
    }
  };

  return (
    <div className="space-y-6">
      {/* Generate Promo Code Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5" />
            Generate Promo Code
          </CardTitle>
          <CardDescription>
            Create promotional codes for credit distribution
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...promoForm}>
            <form
              onSubmit={promoForm.handleSubmit((data) =>
                createPromoMutation.mutate(data),
              )}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={promoForm.control}
                  name="amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Credit Amount</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="Enter amount"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <div className="flex gap-2 mt-2">
                        {QUICK_AMOUNTS.map((amount) => (
                          <Button
                            key={amount}
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => promoForm.setValue("amount", amount)}
                          >
                            {amount.toLocaleString()}
                          </Button>
                        ))}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={promoForm.control}
                  name="expirationDays"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Expiration (Days)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="Days until expiration"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Button
                type="submit"
                disabled={createPromoMutation.isPending}
                className="w-full"
              >
                {createPromoMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Generate Promo Code
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>

      {/* Direct Credit Grant Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Grant Credits Directly
          </CardTitle>
          <CardDescription>
            Directly grant credits to a specific agent
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...grantForm}>
            <form
              onSubmit={grantForm.handleSubmit(onGrantFormSubmit)}
              className="space-y-4"
            >
              <div className="space-y-4">
                <div className="w-full">
                  <Label className="text-sm font-medium mb-2 block">
                    Select Agent
                  </Label>
                  <Popover open={personaOpen} onOpenChange={setPersonaOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={personaOpen}
                        className="w-full justify-between rounded-lg"
                        disabled={isLoadingPersonas}
                      >
                        {selectedPersona?.name ?? "Select an agent..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0">
                      <Command>
                        <CommandInput placeholder="Search agents..." />
                        <CommandList>
                          <CommandEmpty>No agent found.</CommandEmpty>
                          <CommandGroup>
                            {personas
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map((persona) => (
                                <CommandItem
                                  key={persona.persona_id}
                                  value={persona.persona_id}
                                  onSelect={(value) => {
                                    setSelectedPersonaId(value);
                                    setPersonaOpen(false);
                                    grantForm.setValue("agentId", value);
                                  }}
                                  keywords={[persona.name]}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      selectedPersonaId === persona.persona_id
                                        ? "opacity-100"
                                        : "opacity-0",
                                    )}
                                  />
                                  {persona.name}
                                </CommandItem>
                              ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>

                  {grantForm.formState.errors.agentId && (
                    <p className="text-sm font-medium text-red-500 mt-1">
                      {grantForm.formState.errors.agentId.message}
                    </p>
                  )}
                </div>

                <FormField
                  control={grantForm.control}
                  name="amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Credit Amount</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="Enter amount"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <div className="flex gap-2 mt-2">
                        {QUICK_AMOUNTS.map((amount) => (
                          <Button
                            key={amount}
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => grantForm.setValue("amount", amount)}
                          >
                            {amount.toLocaleString()}
                          </Button>
                        ))}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={grantForm.control}
                  name="reason"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Reason (Optional)</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter reason for grant"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Button
                type="submit"
                disabled={grantCreditsMutation.isPending}
                className="w-full"
              >
                {grantCreditsMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Grant Credits
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>

      {/* Promo Codes Table */}
      <Card className="w-1000 mx-auto">
        {" "}
        {/* This sets width to 80% and centers it */}
        <CardHeader>
          <CardTitle>Promo Codes</CardTitle>
          <CardDescription>
            Track and manage promotional credit codes
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingCodes ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Promo Code</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Created At</TableHead>
                  <TableHead>Created By</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Redeemed By Agent</TableHead>
                  <TableHead>Redeemed At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {promoCodes.map((code) => (
                  <TableRow key={code.promo_id}>
                    <TableCell className="font-mono font-medium">
                      {code.promo_id}
                    </TableCell>
                    <TableCell className="text-right">
                      {code.amount.toLocaleString()} credits
                    </TableCell>
                    <TableCell>
                      {new Date(code.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {code.created_by_user ||
                        code.metadata?.created_by_email ||
                        "-"}
                    </TableCell>
                    <TableCell>
                      {code.expiration_date
                        ? new Date(code.expiration_date).toLocaleString()
                        : "Never"}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          code.status === "available" ? "default" : "secondary"
                        }
                        className={cn(
                          "px-2 py-0.5",
                          code.status === "available" &&
                            "bg-green-500/20 text-green-500 border-green-500/20",
                          code.status === "redeemed" &&
                            "bg-blue-500/20 text-blue-500 border-blue-500/20",
                          code.status === "expired" &&
                            "bg-gray-500/20 text-gray-500 border-gray-500/20",
                        )}
                      >
                        {code.status.charAt(0).toUpperCase() +
                          code.status.slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {code.redeemed_by_agent_name ||
                        (code.redeemed_by_agent_id &&
                          `${code.redeemed_by_agent_id}`) ||
                        "-"}
                    </TableCell>
                    <TableCell>
                      {code.redeemed_at
                        ? new Date(code.redeemed_at).toLocaleString()
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
